import React, { useCallback, useEffect, useMemo, useState } from "react";
import Checkbox from "../ui/Checkbox";
import DragNumberInput from "../ui/DragNumberInput";
import TripleOrderedRangeSlider from "../ui/TripleOrderedRangeSlider";
import { useJoints, useSceneActions, useSceneStore } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";

const UNLIMITED_SLIDE_CURRENT_MIN = -20;
const UNLIMITED_SLIDE_CURRENT_MAX = 20;

function jointRangeEndsFromEntry(j) {
  const [a, b] = Array.isArray(j?.range) ? j.range : [-Math.PI, Math.PI];
  return { lo: Number(a), hi: Number(b) };
}

/**
 * Owns `ref` + `range` subscriptions so hinge/slide value and limit thumb drags
 * do not rerender JointPropertiesPanel.
 */
const JointScalarTripleRangeSlider = React.memo(function JointScalarTripleRangeSlider({
  jointUuid,
  disabled,
}) {
  const robotActions = useSceneActions();

  const rangeLo = useJoints(
    useCallback(
      (jm) => {
        const j = jm[jointUuid];
        if (!j) return -Math.PI;
        return jointRangeEndsFromEntry(j).lo;
      },
      [jointUuid]
    )
  );

  const rangeHi = useJoints(
    useCallback(
      (jm) => {
        const j = jm[jointUuid];
        if (!j) return Math.PI;
        return jointRangeEndsFromEntry(j).hi;
      },
      [jointUuid]
    )
  );

  const valueMid = useJoints(
    useCallback(
      (jm) => {
        const j = jm[jointUuid];
        return j ? Number(j.ref) || 0 : 0;
      },
      [jointUuid]
    )
  );

  const jointKind = useJoints(
    useCallback(
      (jm) => {
        const j = jm[jointUuid];
        return j?.kind ?? "hinge";
      },
      [jointUuid]
    ),
    Object.is
  );

  const useLimits = useJoints(
    useCallback(
      (jm) => {
        const j = jm[jointUuid];
        return j?.useLimits ?? "auto";
      },
      [jointUuid]
    ),
    Object.is
  );

  const [slideLimitControls, setSlideLimitControls] = useState({
    jointUuid: null,
    min: false,
    max: false,
  });

  useEffect(() => {
    if (jointKind !== "slide") return;
    setSlideLimitControls({
      jointUuid,
      min: useLimits !== "false",
      max: useLimits !== "false",
    });
  }, [jointUuid, jointKind, useLimits]);

  const lowEnabled = jointKind !== "slide" || slideLimitControls.min;
  const highEnabled = jointKind !== "slide" || slideLimitControls.max;

  const dispatchSlideVisualUpdate = useCallback(
    ({ min = rangeLo, max = rangeHi, minEnabled = lowEnabled, maxEnabled = highEnabled } = {}) => {
      if (jointKind !== "slide") return;
      window.dispatchEvent(
        new CustomEvent("slide-joint-limit-visual-controls-updated", {
          detail: {
            jointUuid,
            min,
            max,
            minEnabled,
            maxEnabled,
          },
        })
      );
    },
    [highEnabled, jointKind, jointUuid, lowEnabled, rangeHi, rangeLo]
  );

  const activeSliderValues = [valueMid];
  if (lowEnabled) activeSliderValues.push(rangeLo);
  if (highEnabled) activeSliderValues.push(rangeHi);
  const activeSliderLo = Math.min(...activeSliderValues);
  const activeSliderHi = Math.max(...activeSliderValues);

  const jointSliderBounds = useMemo(() => {
    if (jointKind === "slide" && (!lowEnabled || !highEnabled)) {
      const minBound = lowEnabled ? Number(rangeLo) : UNLIMITED_SLIDE_CURRENT_MIN;
      const maxBound = highEnabled ? Number(rangeHi) : UNLIMITED_SLIDE_CURRENT_MAX;
      return {
        minBound: Math.min(minBound, maxBound),
        maxBound: Math.max(minBound, maxBound),
      };
    }
    const lo = activeSliderLo;
    const hi = activeSliderHi;
    const span = hi - lo;
    const pad = span > 1e-12 ? span * 0.1 : jointKind === "slide" ? 0.5 : 0.1;
    return { minBound: lo - pad, maxBound: hi + pad };
  }, [activeSliderLo, activeSliderHi, highEnabled, jointKind, lowEnabled, rangeHi, rangeLo]);

  const sliderSpan = jointSliderBounds.maxBound - jointSliderBounds.minBound;
  const sliderStep = sliderSpan / 100 || 0.01;
  const limitThumbStep = Math.max(0.008, sliderSpan / 180);

  const handleMid = useCallback(
    (v) => {
      robotActions.updateJointValue(jointUuid, v);
    },
    [robotActions, jointUuid]
  );

  const handleLow = useCallback(
    (val) => {
      const min = Number(val);
      const j = useSceneStore.getState().joints?.[jointUuid];
      const { hi } = jointRangeEndsFromEntry(j);
      try {
        robotActions.updateJointRange(jointUuid, min, hi);
        dispatchSlideVisualUpdate({ min, max: hi });
      } catch (error) {
        logger.warn("[JointPropertiesPanel] Failed to update joint lower limit", error);
      }
    },
    [dispatchSlideVisualUpdate, robotActions, jointUuid]
  );

  const handleHigh = useCallback(
    (val) => {
      const max = Number(val);
      const j = useSceneStore.getState().joints?.[jointUuid];
      const { lo } = jointRangeEndsFromEntry(j);
      try {
        robotActions.updateJointRange(jointUuid, lo, max);
        dispatchSlideVisualUpdate({ min: lo, max });
      } catch (error) {
        logger.warn("[JointPropertiesPanel] Failed to update joint upper limit", error);
      }
    },
    [dispatchSlideVisualUpdate, robotActions, jointUuid]
  );

  const handleSlideLimitToggle = useCallback(
    (key, checked) => {
      const base =
        slideLimitControls.jointUuid === jointUuid
          ? slideLimitControls
          : { jointUuid, min: false, max: false };
      const nextControls = { ...base, [key]: checked };
      setSlideLimitControls(nextControls);

      const otherKey = key === "min" ? "max" : "min";
      const otherEnabled = !!nextControls[otherKey];
      try {
        robotActions.updateJointUseLimits(jointUuid, checked || otherEnabled ? "true" : "false");
        dispatchSlideVisualUpdate({
          minEnabled: nextControls.min,
          maxEnabled: nextControls.max,
        });
      } catch (error) {
        logger.warn("[JointPropertiesPanel] Failed to update joint limit controls", error);
      }
    },
    [dispatchSlideVisualUpdate, jointUuid, robotActions, slideLimitControls]
  );

  return (
    <>
      <TripleOrderedRangeSlider
        key={jointUuid}
        minBound={jointSliderBounds.minBound}
        maxBound={jointSliderBounds.maxBound}
        limitStep={limitThumbStep}
        valueStep={sliderStep}
        valueLow={rangeLo}
        valueMid={valueMid}
        valueHigh={rangeHi}
        lowEnabled={lowEnabled}
        highEnabled={highEnabled}
        disabled={disabled}
        lowAriaLabel="Joint limit minimum"
        midAriaLabel="Joint current value"
        highAriaLabel="Joint limit maximum"
        showNumberInputs={jointKind !== "slide"}
        onLowChange={handleLow}
        onMidChange={handleMid}
        onHighChange={handleHigh}
      />
      {jointKind === "slide" ? (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-[auto_minmax(0,1fr)] items-end gap-3">
            <Checkbox
              label="Min"
              checked={lowEnabled}
              disabled={disabled}
              onChange={(e) => handleSlideLimitToggle("min", e.target.checked)}
              className="mb-1.5 min-w-16 !px-0 !py-0 text-xs text-gray-300"
            />
            <DragNumberInput
              label=""
              value={lowEnabled ? rangeLo : ""}
              dragValue={rangeLo}
              disabled={disabled || !lowEnabled}
              deferDragUpdates={false}
              dragSensitivity={limitThumbStep}
              dragSnapStep={limitThumbStep}
              onDragValueChange={handleLow}
              inputProps={{ "aria-label": "Joint limit minimum", placeholder: "NA" }}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (Number.isFinite(value)) handleLow(value);
              }}
              inputClassName="w-full min-w-0 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm disabled:opacity-60 placeholder:text-gray-500"
              wrapperClassName="min-w-0"
            />
          </div>
          <div className="grid grid-cols-[auto_minmax(0,1fr)] items-end gap-3">
            <Checkbox
              label="Max"
              checked={highEnabled}
              disabled={disabled}
              onChange={(e) => handleSlideLimitToggle("max", e.target.checked)}
              className="mb-1.5 min-w-16 !px-0 !py-0 text-xs text-gray-300"
            />
            <DragNumberInput
              label=""
              value={highEnabled ? rangeHi : ""}
              dragValue={rangeHi}
              disabled={disabled || !highEnabled}
              deferDragUpdates={false}
              dragSensitivity={limitThumbStep}
              dragSnapStep={limitThumbStep}
              onDragValueChange={handleHigh}
              inputProps={{ "aria-label": "Joint limit maximum", placeholder: "NA" }}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (Number.isFinite(value)) handleHigh(value);
              }}
              inputClassName="w-full min-w-0 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm disabled:opacity-60 placeholder:text-gray-500"
              wrapperClassName="min-w-0"
            />
          </div>
        </div>
      ) : null}
    </>
  );
});

export default JointScalarTripleRangeSlider;
